import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Clock3, LineChart, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePatients } from "@/lib/usePatients";
import { getRuntimeKnowledgeBase } from "@/lib/pharmacology/runtimeKnowledge";
import { buildSessionContext } from "@/domain/sessionContextEngine";
import { SessionJournalPanel } from "@/components/clinical/SessionJournalPanel";
import { SessionContextPanels } from "@/components/clinical/SessionContextPanels";
import { ClinicalMomentPanel } from "@/components/clinical/ClinicalMomentPanel";
import { buildClinicalMoment } from "@/domain/clinicalMomentEngine";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const PATIENT_STORAGE_KEY = "clinicalos-my-space-patient";
const HISTORY_DAYS = 120;

function parseDay(value?: string) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

function localDayKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatDay(date: Date) {
  return new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(date);
}

function minuteLabel(value: number) {
  const hour = Math.floor(value / 60);
  const minute = value % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function dateAtMinute(day: Date, minute: number) {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(minute / 60), minute % 60, 0, 0);
}

export default function JournalDay() {
  const { date: dateParam } = useParams();
  const parsedDay = useMemo(() => parseDay(dateParam), [dateParam]);
  if (!parsedDay || !dateParam) return <Navigate to="/journal" replace />;
  return <JournalDayContent day={parsedDay} dateParam={dateParam} />;
}

function JournalDayContent({ day, dateParam }: { day: Date; dateParam: string }) {
  const { data: patients = [] } = usePatients();
  const qc = useQueryClient();
  const patientId = localStorage.getItem(PATIENT_STORAGE_KEY) ?? "";
  const patient = (patients as any[]).find((row) => row.id === patientId);
  const knowledge = useMemo(() => getRuntimeKnowledgeBase(), []);
  const now = new Date();
  const isToday = localDayKey(day) === localDayKey(now);
  const currentMinute = now.getHours() * 60 + now.getMinutes();
  const [referenceMinute, setReferenceMinute] = useState(() => isToday ? currentMinute : 23 * 60 + 59);

  const dayStartMs = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0).getTime();
  const nextDayMs = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1, 0, 0, 0, 0).getTime();
  const historyStartMs = dayStartMs - HISTORY_DAYS * 24 * 3_600_000;
  const maxMinute = isToday ? currentMinute : 23 * 60 + 59;
  const safeReferenceMinute = Math.min(referenceMinute, maxMinute);
  const referenceMs = dateAtMinute(day, safeReferenceMinute).getTime();

  const { data: allDoses = [], isLoading } = useQuery({
    queryKey: ["journal-day-context", patientId, dateParam],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_dose_logs")
        .select("*, substances(*), substance_formulations(*)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("actual_time", new Date(historyStartMs).toISOString())
        .lt("actual_time", new Date(nextDayMs).toISOString())
        .order("actual_time", { ascending: true })
        .limit(4000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const sessionDoses = useMemo(
    () => (allDoses as any[]).filter((dose) => {
      const at = new Date(dose.actual_time).getTime();
      return at >= dayStartMs && at < nextDayMs;
    }),
    [allDoses, dayStartMs, nextDayMs],
  );

  const dosesUntilReference = useMemo(
    () => (allDoses as any[]).filter((dose) => new Date(dose.actual_time).getTime() <= referenceMs),
    [allDoses, referenceMs],
  );

  const sessionUntilReference = useMemo(
    () => sessionDoses.filter((dose) => new Date(dose.actual_time).getTime() <= referenceMs),
    [sessionDoses, referenceMs],
  );

  const snapshot = useMemo(() => buildSessionContext({
    allDoses: dosesUntilReference,
    sessionDoses: sessionUntilReference,
    at: new Date(referenceMs),
    knowledge,
    bodyWeightKg: patient?.weight_kg == null ? null : Number(patient.weight_kg),
  }), [dosesUntilReference, sessionUntilReference, referenceMs, knowledge, patient?.weight_kg]);

  const { data: prescriptions = [] } = useQuery({
    queryKey: ["journal-active-meds", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data } = await (supabase as any).from("patient_medications")
        .select("free_text_name, brand_name, current_dose, dose_unit, usual_time, frequency, status, substances(name)")
        .eq("patient_id", patientId).is("deleted_at", null);
      return (data ?? []).filter((m: any) => /ativ|uso|active/i.test(String(m.status ?? "")));
    },
  });
  const { data: vitals = [] } = useQuery({
    queryKey: ["journal-day-vitals", patientId, dateParam],
    enabled: !!patientId,
    queryFn: async () => {
      const { data } = await (supabase as any).from("patient_vital_signs")
        .select("measured_at, systolic_bp, diastolic_bp, heart_rate")
        .eq("patient_id", patientId).is("deleted_at", null)
        .gte("measured_at", new Date(referenceMs - 6 * 3_600_000).toISOString())
        .lte("measured_at", new Date(referenceMs).toISOString());
      return data ?? [];
    },
  });
  const moment = useMemo(() => buildClinicalMoment({
    snapshot,
    prescriptions: isToday && safeReferenceMinute >= currentMinute - 5
      ? (prescriptions as any[]).map((m) => ({ name: m.substances?.name ?? m.free_text_name ?? m.brand_name ?? "", dose: m.current_dose ? `${m.current_dose} ${m.dose_unit ?? ""}`.trim() : null, usualTime: m.usual_time, isMaintenance: true })).filter((m) => m.name)
      : [],
    vitals: (vitals as any[]).map((v) => ({ systolic: v.systolic_bp, diastolic: v.diastolic_bp, heartRate: v.heart_rate, measuredAt: v.measured_at })),
  }), [snapshot, prescriptions, vitals, isToday, safeReferenceMinute, currentMinute]);

  async function refresh() {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["journal-day-context", patientId, dateParam] }),
      qc.invalidateQueries({ queryKey: ["journal-doses", patientId] }),
      qc.invalidateQueries({ queryKey: ["journal-body-load", patientId] }),
      qc.invalidateQueries({ queryKey: ["insights-doses", patientId] }),
      qc.invalidateQueries({ queryKey: ["insights-body-load", patientId] }),
    ]);
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-5 pb-5">
      <header className="sticky top-[calc(3.5rem+env(safe-area-inset-top))] z-20 -mx-2 flex items-center gap-2 border-b bg-background/92 px-2 py-2 backdrop-blur-xl sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:backdrop-blur-none">
        <Button asChild variant="outline" size="icon" className="h-11 w-11 shrink-0 rounded-full"><Link to="/journal" aria-label="Voltar ao Journal"><ArrowLeft className="h-5 w-5" /></Link></Button>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Sessão / dia</div>
          <h1 className="truncate text-lg font-semibold capitalize sm:text-2xl">{formatDay(day)}</h1>
        </div>
        <Button asChild variant="outline" size="icon" className="h-11 w-11 shrink-0 rounded-full"><Link to="/curvas" aria-label="Abrir explorador avançado"><LineChart className="h-5 w-5" /></Link></Button>
      </header>

      <Card className="rounded-3xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-primary" /><div className="font-semibold">Referência temporal</div></div>
            <p className="mt-1 text-xs text-muted-foreground">Arraste para ver como curva, carga corporal, interações e alertas mudavam ao longo do dia.</p>
          </div>
          <Badge variant="outline" className="font-mono text-sm">{minuteLabel(safeReferenceMinute)}</Badge>
        </div>
        <input
          className="mt-4 w-full accent-primary"
          type="range"
          min={0}
          max={maxMinute}
          step={5}
          value={safeReferenceMinute}
          onChange={(event) => setReferenceMinute(Number(event.target.value))}
          aria-label="Horário de referência da sessão"
        />
        <div className="mt-1 flex justify-between text-[10px] text-muted-foreground"><span>00:00</span><span>{minuteLabel(maxMinute)}</span></div>
        {isToday ? <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => setReferenceMinute(currentMinute)}><RotateCcw className="mr-1 h-3.5 w-3.5" />Agora</Button> : null}
      </Card>

      {isLoading ? <Card className="rounded-3xl p-6 text-center text-sm text-muted-foreground">Carregando sessão…</Card> : (
        <>
          <SessionJournalPanel
            doses={sessionDoses}
            patientId={patientId}
            sessionId={null}
            title={`${sessionDoses.length} ingestão(ões) · ${dateParam}`}
            referenceNow={new Date(referenceMs)}
            initialWindowHours={28}
            onSaved={refresh}
          />
          <ClinicalMomentPanel moment={moment} />
          <SessionContextPanels snapshot={snapshot} />
        </>
      )}
    </div>
  );
}
